/**
 * Comments Service
 * Handles CRUD operations for comments and attachments
 */

import { supabase } from '../lib/supabase'
import type {
  Comment,
  CommentAttachment,
  CreateCommentData,
  UpdateCommentData,
  EntityType,
  CommentWithAttachments
} from '../types/comment'

const STORAGE_BUCKET = 'comment-attachments'

/**
 * Get comments for an entity
 */
export async function getComments(
  entityType: EntityType,
  entityId: string
): Promise<CommentWithAttachments[]> {
  const { data, error } = await supabase
    .from('comments')
    .select(`
      *,
      attachments:comment_attachments(*)
    `)
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching comments:', error)
    return []
  }

  // Add signed URLs for attachments
  const commentsWithUrls = await Promise.all(
    (data || []).map(async (comment) => {
      const attachmentsWithUrls = await Promise.all(
        (comment.attachments || []).map(async (attachment: CommentAttachment) => {
          const { data: urlData } = await supabase.storage
            .from(STORAGE_BUCKET)
            .createSignedUrl(attachment.storage_path, 3600) // 1 hour expiry

          return {
            ...attachment,
            url: urlData?.signedUrl || ''
          }
        })
      )

      return {
        ...comment,
        attachments: attachmentsWithUrls
      }
    })
  )

  return commentsWithUrls as CommentWithAttachments[]
}

/**
 * Get comment count for an entity
 */
export async function getCommentCount(
  entityType: EntityType,
  entityId: string
): Promise<number> {
  const { count, error } = await supabase
    .from('comments')
    .select('*', { count: 'exact', head: true })
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)

  if (error) {
    console.error('Error fetching comment count:', error)
    return 0
  }

  return count || 0
}

/**
 * Create a new comment
 */
export async function createComment(
  data: CreateCommentData
): Promise<{ success: boolean; comment?: Comment; error?: string }> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'Ej inloggad' }
    }

    // Get user profile for name and role
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('full_name, role')
      .eq('user_id', user.id)
      .single()

    if (!profile) {
      return { success: false, error: 'Kunde inte hämta användarprofil' }
    }

    // Subcontractors can only create public comments
    if (profile.role === 'subcontractor' && data.is_internal) {
      return { success: false, error: 'Du har inte behörighet att skapa interna kommentarer' }
    }

    const { data: comment, error } = await supabase
      .from('comments')
      .insert({
        entity_type: data.entity_type,
        entity_id: data.entity_id,
        user_id: user.id,
        user_name: profile.full_name || 'Okänd användare',
        user_role: profile.role,
        content: data.content,
        is_internal: data.is_internal
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating comment:', error)
      return { success: false, error: 'Kunde inte skapa kommentar' }
    }

    return { success: true, comment }
  } catch (error) {
    console.error('Error creating comment:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Okänt fel'
    }
  }
}

/**
 * Update a comment
 */
export async function updateComment(
  commentId: string,
  data: UpdateCommentData
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('comments')
    .update({ content: data.content })
    .eq('id', commentId)

  if (error) {
    console.error('Error updating comment:', error)
    return { success: false, error: 'Kunde inte uppdatera kommentar' }
  }

  return { success: true }
}

/**
 * Delete a comment
 */
export async function deleteComment(
  commentId: string
): Promise<{ success: boolean; error?: string }> {
  // First get all attachments to delete from storage
  const { data: attachments } = await supabase
    .from('comment_attachments')
    .select('storage_path')
    .eq('comment_id', commentId)

  // Delete attachments from storage
  if (attachments && attachments.length > 0) {
    const paths = attachments.map(a => a.storage_path)
    await supabase.storage
      .from(STORAGE_BUCKET)
      .remove(paths)
  }

  // Delete the comment (cascades to attachments table)
  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId)

  if (error) {
    console.error('Error deleting comment:', error)
    return { success: false, error: 'Kunde inte ta bort kommentar' }
  }

  return { success: true }
}

/**
 * Upload an attachment to a comment
 */
export async function uploadAttachment(
  commentId: string,
  file: File,
  _onProgress?: (progress: number) => void
): Promise<{ success: boolean; attachment?: CommentAttachment; error?: string }> {
  try {
    // Get the comment to verify access and get entity info
    const { data: comment } = await supabase
      .from('comments')
      .select('entity_type, entity_id, is_internal')
      .eq('id', commentId)
      .single()

    if (!comment) {
      return { success: false, error: 'Kommentar hittades inte' }
    }

    // Create a unique file path
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
    const storagePath = `${comment.entity_type}/${comment.entity_id}/${commentId}/${fileName}`

    // Upload to storage
    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (uploadError) {
      console.error('Error uploading file:', uploadError)
      return { success: false, error: 'Kunde inte ladda upp fil' }
    }

    // Create attachment record
    const { data: attachment, error: dbError } = await supabase
      .from('comment_attachments')
      .insert({
        comment_id: commentId,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size,
        storage_path: storagePath
      })
      .select()
      .single()

    if (dbError) {
      // Clean up uploaded file
      await supabase.storage
        .from(STORAGE_BUCKET)
        .remove([storagePath])

      console.error('Error creating attachment record:', dbError)
      return { success: false, error: 'Kunde inte spara bilaga' }
    }

    // Get signed URL
    const { data: urlData } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(storagePath, 3600)

    return {
      success: true,
      attachment: {
        ...attachment,
        url: urlData?.signedUrl || ''
      }
    }
  } catch (error) {
    console.error('Error uploading attachment:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Okänt fel'
    }
  }
}

/**
 * Delete an attachment
 */
export async function deleteAttachment(
  attachmentId: string
): Promise<{ success: boolean; error?: string }> {
  // Get the attachment to find storage path
  const { data: attachment } = await supabase
    .from('comment_attachments')
    .select('storage_path')
    .eq('id', attachmentId)
    .single()

  if (!attachment) {
    return { success: false, error: 'Bilaga hittades inte' }
  }

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .remove([attachment.storage_path])

  if (storageError) {
    console.error('Error deleting file from storage:', storageError)
  }

  // Delete from database
  const { error } = await supabase
    .from('comment_attachments')
    .delete()
    .eq('id', attachmentId)

  if (error) {
    console.error('Error deleting attachment:', error)
    return { success: false, error: 'Kunde inte ta bort bilaga' }
  }

  return { success: true }
}

/**
 * Subscribe to comment changes for an entity
 */
export function subscribeToComments(
  entityType: EntityType,
  entityId: string,
  callback: (payload: { eventType: string; comment: Comment }) => void
) {
  const subscription = supabase
    .channel(`comments:${entityType}:${entityId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'comments',
        filter: `entity_type=eq.${entityType},entity_id=eq.${entityId}`
      },
      (payload) => {
        callback({
          eventType: payload.eventType,
          comment: payload.new as Comment
        })
      }
    )
    .subscribe()

  return () => {
    subscription.unsubscribe()
  }
}

/**
 * Get a download URL for an attachment
 */
export async function getAttachmentDownloadUrl(
  storagePath: string
): Promise<string | null> {
  const { data } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storagePath, 3600) // 1 hour expiry

  return data?.signedUrl || null
}

/**
 * Create a comment with @mention support
 * Parses mentions from content and creates notifications
 */
export async function createCommentWithMentions(
  data: CreateCommentData,
  mentionedUserIds: string[]
): Promise<{ success: boolean; comment?: Comment; error?: string }> {
  // First create the comment
  const result = await createComment(data)

  if (!result.success || !result.comment) {
    return result
  }

  // If there are mentions, create notifications
  if (mentionedUserIds.length > 0) {
    try {
      // Import dynamically to avoid circular dependency
      const { createMentionNotifications } = await import('./notifications')

      // Get current user info
      const { data: { user } } = await supabase.auth.getUser()
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name')
        .eq('user_id', user?.id)
        .single()

      // Get mentioned users' info (include role to filter for internal comments)
      const { data: mentionedUsers } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, role')
        .in('user_id', mentionedUserIds)

      if (mentionedUsers && user && profile) {
        // For internal comments, only notify admins (TGs can't see internal comments)
        const usersToNotify = data.is_internal
          ? mentionedUsers.filter(u => u.role === 'admin')
          : mentionedUsers

        if (usersToNotify.length > 0) {
          const mentions = usersToNotify.map(u => ({
            userId: u.user_id,
            userName: u.full_name,
            startIndex: 0,
            endIndex: 0
          }))

          await createMentionNotifications(
            mentions,
            result.comment.id,
            data.entity_type,
            data.entity_id,
            user.id,
            profile.full_name || 'Okänd användare'
          )
        }
      }
    } catch (error) {
      console.error('Error creating mention notifications:', error)
      // Don't fail the comment creation if notifications fail
    }
  }

  return result
}
